/**
 * CaptureActionRow — standardised button row for all capture panels
 * (ported from Noteble — ADR-022).
 *
 * Layout:  ...center buttons... [Capture]  |  [Discard]
 */

import React from 'react';
import { Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CaptureActionRowProps {
  onSend?: () => void;
  onDiscard: () => void;
  sendDisabled?: boolean;
  sendLabel?: string;
  discardLabel?: string;
  /** Extra buttons rendered between Delete and Send */
  children?: React.ReactNode;
  className?: string;
}

const actionBtnBase =
  'flex flex-col items-center gap-0.5 w-12 py-0.5 rounded-2xl transition-all duration-200 active:scale-95';

const pillBase =
  'relative flex items-center justify-center w-10 h-6 rounded-full transition-colors duration-200';

export const CaptureActionRow: React.FC<CaptureActionRowProps> = ({
  onSend,
  onDiscard,
  sendDisabled,
  sendLabel = 'Capture',
  discardLabel = 'Discard',
  children,
  className,
}) => (
  <div className={cn('flex items-center gap-2 mt-4', className)}>
    {/* Center — extra buttons + Capture */}
    <div className="flex-1 flex items-center justify-center gap-2">
      {children}
      {onSend && (
        <button
          type="button"
          onClick={onSend}
          disabled={sendDisabled}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 active:scale-95',
            'bg-blue-600 text-white shadow-sm ring-1 ring-blue-600/30 hover:bg-blue-700 hover:shadow-md',
            sendDisabled && 'opacity-40 cursor-not-allowed',
          )}
          aria-label={sendLabel}
        >
          {sendLabel}
        </button>
      )}
    </div>

    {/* Discard — right */}
    <button
      type="button"
      onClick={onDiscard}
      className={cn(actionBtnBase, 'text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300')}
      aria-label={discardLabel}
    >
      <div className={cn(pillBase, 'bg-transparent')}>
        <Trash2 className="w-4 h-4" strokeWidth={1.6} />
      </div>
    </button>
  </div>
);
